import "./list-employee-branch.scss";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Sidebar } from "primereact/sidebar";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";

import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import ErrorSection from "@shared/ui/error-section/error-section";
import SearchInput from "@shared/ui/search-input/search-input";
import HeaderContent from "@shared/ui/header-content/header-content";
import { useQyGetEmployeeBranch } from "@core/query/employee-branch.query";
import { GetEmployeeBranchModel } from "@core/model/query-model-employee-branch";
import { TableSort } from "@core/model/table-sort";
import { FilterEmployeeBranch } from "../filter-employee-branch/filter-employee-branch";
import { useFilterEmployeeBranchContext } from "../filter-employee-branch/filter-employee-branch.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import TextBlock from "@shared/ui/text-block/text-block";

export function ListEmployeeBranch() {
  // THIS IS THE LOCAL DECLARATION
  const navigate = useNavigate();
  const { formatDate, formatDateTimeV2 } = useDateTimeContext();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, getFilterEntityValues } =
    useFilterEmployeeBranchContext();

  // THIS IS THE QUERY DECLARATION
  const {
    data: employeeBranchResponse,
    isLoading,
    isError,
    error,
  } = useQyGetEmployeeBranch(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    getFilterEntityValues()
  );

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: GetEmployeeBranchModel) => {
    navigate(`${item.code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };

  // THIS IS THE LOCAL RENDERS
  const logColumn = (column: any) => {
    const createdAt = (
      <TextBlock
        label="Created At"
        value={formatDateTimeV2(column["created_at"])}
      ></TextBlock>
    );
    const createdBy = (
      <TextBlock
        label="Created By"
        value={`${column["created_first_name"]} ${column["created_last_name"]}`}
      ></TextBlock>
    );
    const updatedAt = (
      <TextBlock
        label="Updated At"
        value={formatDateTimeV2(column["updated_at"])}
      ></TextBlock>
    );
    const updatedBy = (
      <TextBlock
        label="Updated By"
        value={`${column["updated_first_name"]} ${column["updated_last_name"]}`}
      ></TextBlock>
    );
    return (
      <div>
        {createdAt} {createdBy} {updatedAt} {updatedBy}
      </div>
    );
  };
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterEmployeeBranch />
      </Sidebar>
    </section>
  );
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{employeeBranchResponse?.count}</b>
      </h5>

      <DataTable
        value={employeeBranchResponse?.data}
        selectionMode="single"
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column field="branch_name" header="Branch" sortable></Column>
        <Column
          body={(cell) => formatDate(cell.effectivity_date)}
          header="Effectivity Date"
          sortable
        ></Column>
        <Column body={(cell) => logColumn(cell)} header="Log" sortable></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={employeeBranchResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="EmployeeBranch" className="employee-branch">
      <HeaderContent title="Branch Transfers">
        <span className="flex gap-2">
          <Button label="New" onClick={handleNew} />
        </span>
      </HeaderContent>

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default ListEmployeeBranch;
